<template>
  <v-card class="ma-4">
    <v-toolbar>
      <v-toolbar-title>Off-Campus Submission Processor</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <p>This page will pull a list of all pending form submissions which need to be processed, and allow the user to either edit and either approve or deny the submission.</p>
      <p>Both approving and denying the submission will add a copy of the information to the SERVE timeline for the student, and mark the form submission as processed. But if the submission is approved, then the hours will be added to the student's hours for the submission period.</p>
      <v-data-table :items="submissions" :headers="headers">
        <template v-slot:item.actions="{ item }">
          <v-icon v-if="item.status === 'Approved'" color="success">fas fa-check</v-icon>
          <v-icon v-else-if="item.status === 'Denied'" color="error">fas fa-times</v-icon>
          <v-menu v-else>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon>
                <v-icon>fal fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="updateEntry('Approved', item)">
                <v-list-item-title>Approve Submission</v-list-item-title>
              </v-list-item>
              <v-list-item @click="updateEntry('Denied', item)">
                <v-list-item-title>Deny Submission</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>
<script>
import { computed, onMounted, ref } from '@vue/composition-api'

const formId = '689240a9143605c1ec1f5819'

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const submissions = ref([])
    const headers = ref([
      { text: 'Banner ID', value: 'bannerId' },
      { text: 'First Name', value: 'first' },
      { text: 'Last Name', value: 'last' },
      { text: 'Where', value: 'where' },
      { text: 'Date', value: 'date' },
      { text: 'Hours', value: 'hours' },
      { text: 'Supervisor', value: 'supervisor' },
      { text: 'Supervisor Contact Info', value: 'superInfo' },
      { text: 'Brief Description', value: 'desc' },
      { text: 'Actions', value: 'actions' }
    ])

    onMounted(async () => {
      const aggregate = [
        { $match: { 'form.base': formId, status: 'Submitted' } },
        { $addFields: {
          bannerId: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: ['$$item.field', 'Banner ID'] } } },
          first: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: ['$$item.field', 'First Name'] } } },
          last: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: ['$$item.field', 'Last Name'] } } },
          hours: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: [ '$$item.field', 'How many hours did you serve?' ] } } },
          where: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: [ '$$item.field', 'Where did you serve?' ] } } },
          date: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: [ '$$item.field', 'When did you serve?' ] } } },
          supervisor: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: ['$$item.field', 'Supervisor'] } } },
          superInfo: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: [ '$$item.field', 'Supervisor Contact Info' ] } } },
          desc: { $filter: { input: '$dataArr', as: 'item', cond: { $eq: [ '$$item.field', 'Brief Description of Service' ] } } }
        } },
        { $project: {
          user: 1,
          status: 1,
          bannerId: { $first: '$bannerId' },
          first: { $first: '$first' },
          last: { $first: '$last' },
          hours: { $first: '$hours' },
          where: { $first: '$where' },
          date: { $first: '$date' },
          supervisor: { $first: '$supervisor' },
          superInfo: { $first: '$superInfo' },
          desc: { $first: '$desc' }
        } },
        { $addFields: {
          bannerId: '$bannerId.value',
          first: '$first.value',
          last: '$last.value',
          hours: '$hours.value',
          where: '$where.value',
          date: '$date.value',
          supervisor: '$supervisor.value',
          superInfo: '$superInfo.value',
          desc: '$desc.value'
        } },
        { $lookup: {
          from: 'users',
          localField: 'user',
          foreignField: '_id',
          as: 'user'
        } },
        { $addFields: { user: { $first: '$user' } } },
        { $addFields: { pidm: '$user.pidm' } },
        { $lookup: {
          from: 'Serve-Students',
          localField: 'pidm',
          foreignField: 'pidm',
          as: 'serve',
          pipeline: [
            { $match: { assignment: 'Off-Campus' } }
          ]
        } },
        { $addFields: { serve: { $first: '$serve' } } }
      ]
      const { data } = await root.$feathers.service('forms/submission').find({ query: { aggregate } })
      submissions.value = data
    })

    async function updateEntry (status, { _id, bannerId, date, where, serve, hours }) {
      // If serve is not loaded, then do a lookup to find the serve record
      if (!serve) {
        const { data } = await root.$feathers.service('serve/students').find({ query: { bannerId, assignment: 'Off-Campus' } })
        if (data.length > 0) {
          serve = data[0]
        } else {
          alert('There was an error approving the time: A SERVE entry was not found for this person for the Off-Campus assignment')
          return
        }
      }
      // Update the student's hours
      const $patch = {
        $push: {
          history: {
            date,
            hours,
            desc: where,
            addedBy: user.value.username,
            status: status === 'Approved' ? 'Completed' : 'Cancelled'
          }
        }
      }
      if (status === 'Approved') $patch.$inc = { hours }
      await root.$feathers.service('serve/student').patch(serve._id, $patch)
      // Mark the submission as processed
      await root.$feathers.service('forms/submission').patch(_id, { status })
      const index = submissions.value.findIndex(({ _id: id }) => id === _id)
      if (index >= 0) {
        submissions.value.splice(index, 1, { ...submissions.value[index], status })
      }
    }

    return {
      submissions,
      headers,
      updateEntry
    }
  }
}
</script>
